import { Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';
import { KnownErrorInstrumentationEvents } from '@office-start/telemetry-types';

import { LinkTarget } from '../../shared/constants/link-target.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { ErrorDialogErrorCategory, ErrorDialogErrorType } from '../../shared/models/error-dialog.model';
import { ErrorDialogService } from '../../shared/services/error-dialog.service';
import { LaunchAction, UrlLauncherService } from '../../shared/services/url-launcher.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';

/**
 * PWA URL launcher.
 */
@Injectable()
export abstract class PwaUrlLauncherService implements UrlLauncherService {

    private static readonly errorDialogMap = new Map<LaunchAction, ErrorDialogErrorType>([
        [LaunchAction.GoToBlankDoc, ErrorDialogErrorType.None], // Blank doc error shown in create-document.service
        [LaunchAction.CreateNewFallback, ErrorDialogErrorType.None], // Landing page error shown in create-document.service
        [LaunchAction.Upload, ErrorDialogErrorType.None], // TODO: Show custom error when uploaded doc can't be opened
        [LaunchAction.CreateFromDocument, ErrorDialogErrorType.None], // TODO: Show error when we fail to open a Sway created via doc upload
        [LaunchAction.LaunchProtocol, ErrorDialogErrorType.None], // Native bridge handles protocol errors
        [LaunchAction.CreateFileWithTemplate, ErrorDialogErrorType.None] // Local template errors fall back to blank doc
    ]);

    constructor(private readonly capabilitiesService: CapabilitiesService, private readonly errorDialogService: ErrorDialogService, private readonly instrumentationService: InstrumentationService) {
    }

    public abstract supportsProtocol(protocol: string): Promise<boolean>;

    public async openUrl(url: string, action: LaunchAction, _linkTarget?: LinkTarget, _width?: number, _height?: number): Promise<void> {
        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
            return import('@office-start/launcher-service').then(
                launchSvc => {
                    launchSvc.launchUrl(url, {action}).catch( error => {
                        this.instrumentFailedLaunch(action, error);
                        this.errorDialogService.showErrorDialog({
                            category: ErrorDialogErrorCategory.OpenFailed,
                            error: PwaUrlLauncherService.errorDialogMap.has(action) ? PwaUrlLauncherService.errorDialogMap.get(action) : ErrorDialogErrorType.Generic,
                            inputText: url
                        });
                        return Promise.reject(error);
                    });
                }
            );
        } else {
            return this.launchUrl(url).catch( error => {
                this.instrumentFailedLaunch(action, error);
                this.errorDialogService.showErrorDialog({
                    category: ErrorDialogErrorCategory.OpenFailed,
                    error: PwaUrlLauncherService.errorDialogMap.has(action) ? PwaUrlLauncherService.errorDialogMap.get(action) : ErrorDialogErrorType.Generic,
                    inputText: url
                });
                return Promise.reject(error);
            });
        }
    }

    protected abstract launchUrl(url: string, linkTarget?: LinkTarget, width?: number, height?: number): Promise<void>;

    private instrumentFailedLaunch(action: LaunchAction, error: string = '') {
        const navParams: InstrumentationParams = {};
        navParams[InstrumentationParam.Action] = action;
        navParams[InstrumentationParam.Error] = error;
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaNavigationFailed, navParams, [], DataSecurityLevel.Enhanced);
        logError({ eventName: KnownErrorInstrumentationEvents.PwaNavigationFailed, message: error, debugInfo: action });
    }
}
